import { ItemsReturnedBreakdown } from '@/domain/returns/returns'
import { ApexOptions } from 'apexcharts'
import Chart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'

interface ReturnsByCompensationMethodsChartProps {
  names: string[]
  itemsPurchased: number[]
  itemsReturned: ItemsReturnedBreakdown[]
  chartMetric:
    | 'RETURN_RATE_CHART_METRIC_UNSPECIFIED'
    | 'RETURN_RATE_CHART_METRIC_QUANTITY'
    | 'RETURN_RATE_CHART_METRIC_AMOUNT'
  currency: string
}

export const ReturnsByCompensationMethodsChart: React.FC<
  ReturnsByCompensationMethodsChartProps
> = ({
  names,
  itemsReturned,
  chartMetric = 'RETURN_RATE_CHART_METRIC_QUANTITY',
  currency
}) => {
  const { t, i18n } = useTranslation()
  const options: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      stacked: true,
      toolbar: {
        show: false
      }
    },
    stroke: {
      width: [0, 0, 0, 0],
      colors: ['transparent', 'transparent', 'transparent', 'transparent'],
      curve: 'straight'
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [4, 5],
      background: {
        enabled: true,
        borderWidth: 0
      },
      formatter: (val: number) =>
        chartMetric === 'RETURN_RATE_CHART_METRIC_QUANTITY'
          ? val.toLocaleString()
          : val.toLocaleString(i18n.language, {
              style: 'currency',
              currency
            })
    },
    plotOptions: {
      bar: {
        horizontal: false
      }
    },
    xaxis: {
      categories: names,
      labels: {
        style: {
          fontSize: '10px'
        }
      }
    },
    fill: {
      opacity: 1
    },
    colors: ['#073777', '#adc7ff', '#2e79e2'],
    yaxis: [
      {
        seriesName: [
          t('returns_analytics.refunds'),
          t('returns_analytics.store_credit'),
          t('returns_analytics.exchanges')
        ],
        min: 0,
        labels: {
          formatter: (val: number) =>
            chartMetric === 'RETURN_RATE_CHART_METRIC_QUANTITY'
              ? val.toLocaleString()
              : val.toLocaleString(i18n.language, {
                  style: 'currency',
                  currency,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })
        }
      }
    ],
    legend: {
      position: 'bottom',
      horizontalAlign: 'center'
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) =>
          chartMetric === 'RETURN_RATE_CHART_METRIC_QUANTITY'
            ? val.toLocaleString()
            : val.toLocaleString(i18n.language, {
                style: 'currency',
                currency
              })
      }
    }
  }

  const series = [
    {
      name: t('returns_analytics.refunds'),
      group: 'actual',
      data: itemsReturned.map((item) => item.returned_refunds)
    },
    {
      name: t('returns_analytics.store_credit'),
      group: 'actual',
      data: itemsReturned.map((item) => item.returned_gift_cards)
    },
    {
      name: t('returns_analytics.exchanges'),
      group: 'actual',
      data: itemsReturned.map((item) => item.returned_exchanges)
    }
  ]

  return (
    <div id="chart">
      <Chart
        options={options}
        series={series}
        type="bar"
        height={350}
        width="100%"
      />
    </div>
  )
}
